import { getFilePath, getUrlParamValue, isNotNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

const isLocalDevHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
const shareBaseOrigin = 'https://wagner-living.de';

function readUrlRuntimeValue(key: keyof PconRuntimeConfig): string | undefined {
    const fromUrl = getUrlParamValue(window.location.href, key);
    if (isNotNullOrEmpty(fromUrl)) {
        return fromUrl;
    }

    return undefined;
}

function readWindowRuntimeValue(key: keyof PconRuntimeConfig): string | undefined {
    const fromWindow = window.__PCON_CONFIG__?.[key];
    if (typeof fromWindow === 'string' && fromWindow.length > 0) {
        return fromWindow;
    }

    return undefined;
}

function normalizeBaseUrl(url: string): string {
    return url.replace(/\/+$/, '');
}

function readRuntimeValue(key: keyof PconRuntimeConfig): string | undefined {
    return readUrlRuntimeValue(key) ?? readWindowRuntimeValue(key);
}

function resolveAssetBaseUrlFromScripts(): string | undefined {
    const scripts = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'));
    const matchingScript = scripts.find((script) => script.id === 'pcon-index-js')
        ?? scripts.slice().reverse().find((script) => {
            try {
                return new URL(script.src, window.location.href).pathname.endsWith('/main.js');
            } catch {
                return false;
            }
        });

    if (matchingScript == null || !isNotNullOrEmpty(matchingScript.src)) {
        return undefined;
    }

    try {
        return normalizeBaseUrl(new URL('.', matchingScript.src).href);
    } catch {
        return undefined;
    }
}

function resolveAssetBaseUrl(): string {
    const fromWindow = readWindowRuntimeValue('assetBaseUrl');
    if (isNotNullOrEmpty(fromWindow)) {
        return normalizeBaseUrl(fromWindow);
    }

    const fromUrl = readUrlRuntimeValue('assetBaseUrl');
    if (isNotNullOrEmpty(fromUrl)) {
        return normalizeBaseUrl(fromUrl);
    }

    const fromScripts = resolveAssetBaseUrlFromScripts();
    if (isNotNullOrEmpty(fromScripts)) {
        return fromScripts;
    }

    return normalizeBaseUrl(getFilePath(window.location.pathname));
}

export const runtimeConfig = {
    assetBaseUrl: resolveAssetBaseUrl(),
    bootstrapBaseArticleNumber: readRuntimeValue('bootstrapBaseArticleNumber'),
    bootstrapBaseArticleName: readRuntimeValue('bootstrapBaseArticleName'),
    bootstrapLanguage: readRuntimeValue('bootstrapLanguage'),
    // Wagner upload allows localhost CORS, but selection lookup does not, so keep GET proxied in local dev.
    shareUploadUrl: readRuntimeValue('shareUploadUrl') ?? `${shareBaseOrigin}/wp-json/pcon/v1/upload`,
    shareGetUrlBase: readRuntimeValue('shareGetUrlBase') ?? (isLocalDevHost ? '/wp-json/pcon/v1/get' : `${shareBaseOrigin}/wp-json/pcon/v1/get`),
    pdfTemplatePath: readRuntimeValue('pdfTemplatePath') ?? '/EAIWS/plugins/Reporter/template/wcf_Wagner_5fc7a7fcd1644_hwgd4/generate',
    pdfViewId: readRuntimeValue('pdfViewId') ?? '2e21f798-2d8d-4af1-9fba-748ab606195a',
    pdfViewName: readRuntimeValue('pdfViewName') ?? 'compact',
    pdfDisplayMode: readRuntimeValue('pdfDisplayMode') ?? 'Sorted',
    pdfMergeMode: readRuntimeValue('pdfMergeMode') ?? 'Compact',
    defaultCatalogId: readRuntimeValue('defaultCatalogId') ?? 'wag:0',
    defaultBaseArticleNumber: readRuntimeValue('defaultBaseArticleNumber') ?? 'WCY',
    startupVariantCode: readRuntimeValue('startupVariantCode'),
    anchor: readRuntimeValue('anchor') ?? '#configurator',
};

export function buildAssetUrl(assetPath: string): string {
    const normalizedAssetPath = assetPath.replace(/^\/+/, '');
    return `${runtimeConfig.assetBaseUrl}/${normalizedAssetPath}`;
}

export function buildShareGetUrl(selectionId: string): string | null {
    if (runtimeConfig.shareGetUrlBase == null) {
        return null;
    }
    const separator = runtimeConfig.shareGetUrlBase.endsWith('/') ? '' : '/';
    return `${runtimeConfig.shareGetUrlBase}${separator}${encodeURIComponent(selectionId)}`;
}

export function buildPdfTemplateUrl(sessionBaseUrl: string): string | null {
    if (runtimeConfig.pdfTemplatePath == null) {
        return null;
    }
    if (/^https?:\/\//.test(runtimeConfig.pdfTemplatePath)) {
        return runtimeConfig.pdfTemplatePath;
    }

    const normalizedBaseUrl = sessionBaseUrl.endsWith('/') ? sessionBaseUrl.slice(0, -1) : sessionBaseUrl;
    const normalizedTemplatePath = runtimeConfig.pdfTemplatePath.startsWith('/')
        ? runtimeConfig.pdfTemplatePath
        : `/${runtimeConfig.pdfTemplatePath}`;

    return `${normalizedBaseUrl}${normalizedTemplatePath}`;
}
